import { LookerEmbedSDK } from "@looker/embed-sdk";
import clsx from "clsx";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDownIcon,
  CircleUser,
  ClipboardIcon,
  Clock,
  Edit,
  Link,
  Loader2,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  Text,
  Trash,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "react-image-crop/dist/ReactCrop.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FixedSizeList } from "react-window";
import { v4 } from "uuid";
import { EditElement } from "../components/EditElement";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Spinner } from "../components/ui/spinner";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "../components/ui/use-toast";
import { useFullDocument } from "../hooks/useFullDocument";
import { useKbs } from "../hooks/useKbs";
import { cn } from "../lib/utils";
import { addKb } from "../services/addKb";
import { addSlide } from "../services/addSlide";
import { deleteSlide } from "../services/deleteSlide";
import { editDocument } from "../services/editDocument";
import { editQuestion as eQuestion } from "../services/editQuestion";
import { editSlide } from "../services/editSlide";
import { getDocumentLink } from "../services/getDocumentLink";
import { getPreviews } from "../services/getPreviews";
import { getSlide } from "../services/getSlide";
import { removeKb } from "../services/removeKb";
import { syncPresentation } from "../services/syncPresentation";
import { syncSingleSlide } from "../services/syncSingleSlide";
import { SlideElementData } from "../types";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";

type SlideElement = "text" | "image" | "chart" | "table" | null;

function ChooseSlideElementType(props: {
  setShowSelectType: React.Dispatch<React.SetStateAction<boolean>>;
  setPatch: React.Dispatch<React.SetStateAction<any>>;
  closeSlidePopup: () => void;
  setSelectedType: React.Dispatch<React.SetStateAction<SlideElement>>;
  setNewElement: React.Dispatch<React.SetStateAction<SlideElementData>>;
  fullDocument: any;
}) {
  return (
    <div
      className="bg-white absolute right-0 top-2 h-[98vh] rounded-l-lg w-3/4 py-5 px-10 overflow-y-scroll slide-in"
      onClick={(e) => e.stopPropagation()}
      style={{ transform: "translateY(-50%)" }}
    >
      <div className="flex items-center">
        <div
          className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-100 transition-all"
          role="button"
          onClick={props.closeSlidePopup}
        >
          <X size={15} />
        </div>
        <div className="ml-5">
          <span className="text-zinc-500">
            {props.fullDocument.document.name} /
          </span>{" "}
          Add Element
        </div>
      </div>

      <div className="mt-10">
        <button
          className="bg-zinc-100 px-2 rounded-md hover:bg-zinc-200 transition-all text-zinc-500 py-1 flex items-center gap-2 text-sm"
          onClick={() => {
            props.setShowSelectType(false);
          }}
        >
          <ArrowLeft size={14} />
          Go back
        </button>
        <div>
          <p className="text-lg mt-5">Select type</p>
          <p className="text-zinc-500">
            Select the type of element you want to add
          </p>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <div
            role="button"
            onClick={() => {
              props.setShowSelectType(false);
              props.setNewElement({
                id: v4(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                min_chars: 0,
                max_chars: 0,
                placeholder: "",
                instruction: "",
                attributes: {},
              });
            }}
            className="w-[120px] h-[120px] border rounded-md flex items-center justify-center flex-col gap-2 blue-hover-shadow hover:border-[#0D33F1] transition-all"
          >
            <img className="w-8 aspect-square" src="/titlecase.png" alt="" />
            <p>Text</p>
          </div>
          <div
            role="button"
            onClick={() => {
              props.setShowSelectType(false);
              props.setNewElement({
                id: v4(),
                type: "company_domain",
                alt_text: "",
                url: "",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                attributes: {
                  category: "audience-company-logo",
                  ideal_theme: "default",
                  ideal_type: "default",
                },
                chart_sheet_name: null,
              });
            }}
            className="w-[120px] h-[120px] border rounded-md flex items-center justify-center flex-col gap-2 blue-hover-shadow hover:border-[#0D33F1] transition-all"
          >
            <img className="w-8 aspect-square" src="/image2.png" alt="" />
            <p>Image</p>
          </div>
          <div
            role="button"
            className="w-[120px] h-[120px] border rounded-md flex items-center justify-center flex-col gap-2 blue-hover-shadow hover:border-[#0D33F1] transition-all"
            onClick={() => {
              props.setShowSelectType(false);
              props.setNewElement({
                id: v4(),
                spreadsheet_google_document_id: "",
                prompt: "",
              });
            }}
          >
            <img className="w-8 aspect-square" src="/area_chart.png" alt="" />
            <p>Chart</p>
          </div>
          {/* {user && user.organization_id === "flashdocs.ai" && ( */}
          <div
            onClick={() => {
              props.setShowSelectType(false);
              props.setNewElement({
                id: v4(),
                alt_text: "",
                num_rows: 0,
                num_columns: 0,
                table_height: 0,
                table_width: 0,
              });
            }}
            role="button"
            className="w-[120px] h-[120px] border rounded-md flex items-center justify-center flex-col gap-2 blue-hover-shadow hover:border-[#0D33F1] transition-all"
          >
            <img className="w-8 aspect-square" src="/hub.png" alt="" />
            <p>Table</p>
          </div>
          {/* )} */}
        </div>
      </div>
    </div>
  );
}

function ChooseIntegration(props: {
  selectedDashboard: string;
  dashboards: Array<any>;
  fullDocument: any;
  integrations: Array<any>;
  chosenIntegration: string;
  loadingDashboards: boolean;
  closeSlidePopup: () => void;
  setConfigureLooker: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedDashboard: React.Dispatch<React.SetStateAction<string>>;
  setChosenIntegration: React.Dispatch<React.SetStateAction<string>>;
  setSelectedType: React.Dispatch<React.SetStateAction<SlideElement>>;
}) {
  const [dashboardQuery, setDashboardQuery] = useState<string>("");
  const dashboards = dashboardQuery
    ? props.dashboards.filter((d) => {
        const stringOfDashboardData = JSON.stringify(d);
        return stringOfDashboardData
          .toLowerCase()
          .includes(dashboardQuery.toLowerCase());
      })
    : props.dashboards;

  const [open, setOpen] = useState<boolean>(false);
  const selectedDashboardData = props.dashboards.find(
    (d) => d.id === props.selectedDashboard
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const dashboardList = document.getElementById("dashboard-list");
      const dashboardTrigger = document.getElementById("dashboard-trigger");

      if (dashboardTrigger && dashboardTrigger.contains(event.target as Node)) {
        return;
      }

      if (dashboardList && !dashboardList.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div
      className="bg-white absolute right-0 top-2 h-[98vh] rounded-l-lg w-3/4 py-5 px-10 overflow-y-scroll slide-in"
      onClick={(e) => e.stopPropagation()}
      style={{ transform: "translateY(-50%)" }}
    >
      <div className="flex items-center">
        <div
          className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-100 transition-all"
          role="button"
          onClick={props.closeSlidePopup}
        >
          <X size={15} />
        </div>
        <div className="ml-5">
          <span className="text-zinc-500">
            {props.fullDocument.document.name} / {"Add Element"} /
          </span>{" "}
          Integration
        </div>
      </div>
      <div className="mt-10">
        <button
          className="bg-zinc-100 px-2 rounded-md hover:bg-zinc-200 transition-all text-zinc-500 py-1 flex items-center gap-2 text-sm"
          onClick={() => {
            props.setSelectedType(null);
          }}
        >
          <ArrowLeft size={14} />
          Go back
        </button>
      </div>

      <div className="mt-5 flex flex-col justify-start items-start gap-3">
        <div className="w-[400px]">
          <label htmlFor="data-source">Select data source</label>
          <Select
            onValueChange={(v) => {
              props.setChosenIntegration(v);
            }}
          >
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent className="p-1">
              <SelectGroup>
                {props.integrations.flatMap((d) => (
                  <SelectItem value={d.id}>
                    <div className="flex items-center gap-3">
                      <img
                        className="w-4"
                        src="/google-looker-logo.png"
                        alt="lookerlogo"
                      />
                      Looker
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[400px]">
          <label htmlFor="data-source">Select dashboard</label>
          <div className="relative">
            <div
              id="dashboard-trigger"
              onClick={() => setOpen(!open)}
              className="mt-2 border border-zinc-200 rounded-md p-3 flex items-center justify-between hover:bg-zinc-100 transition-all cursor-pointer"
            >
              <div>
                {selectedDashboardData
                  ? selectedDashboardData.category +
                    " - " +
                    selectedDashboardData.title
                  : "Select a dashboard"}
              </div>
              <div>
                <ChevronsUpDownIcon />
              </div>
            </div>

            {open && (
              <div
                className="p-1 border border-zinc-200 rounded-md absolute w-[400px] bg-white"
                id="dashboard-list"
              >
                <div>
                  <div className="my-3 px-3">
                    <Input
                      value={dashboardQuery}
                      onChange={(e) => {
                        setDashboardQuery(e.target.value);
                      }}
                      placeholder="Search dashboards by title, category or id..."
                    />
                  </div>
                </div>

                <div className="fixed-list-container px-3 h-full">
                  {dashboards.length === 0 && !props.loadingDashboards && (
                    <div className="p-3 w-full flex items-center justify-center">
                      No dashboards found
                    </div>
                  )}
                  {props.loadingDashboards && dashboards.length === 0 && (
                    <div className="p-3 w-full flex items-center justify-center">
                      <LoaderCircle size={30} className="animate-spin" />
                    </div>
                  )}
                  <FixedSizeList
                    itemData={dashboards.sort((a, b) =>
                      a.id.localeCompare(b.id)
                    )}
                    width={"100%"}
                    height={350}
                    itemCount={dashboards.length}
                    itemSize={50}
                  >
                    {({ index, style }: any) => (
                      <div
                        onClick={() => {
                          props.setSelectedDashboard(dashboards[index].id);
                          setOpen(false);
                        }}
                        className="hover:bg-zinc-100 cursor-pointer rounded-md flex items-center justify-start px-3 py-2"
                        key={dashboards[index].id}
                        style={{
                          ...style,
                        }}
                      >
                        <div className="flex items-center">
                          {dashboards[index].category} -{" "}
                          {dashboards[index].title}
                        </div>
                      </div>
                    )}
                  </FixedSizeList>
                </div>
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={() => {
            props.setConfigureLooker(true);
          }}
          className="mt-4"
        >
          Next +
        </Button>
      </div>
    </div>
  );
}

function PreviewImage(props: {
  documentId: string;
  onClick: () => void;
  syncSlides: boolean | string;
  // link: string;
  // setLink: any;
}) {
  const [hover, setHover] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [slideId, setSlideId] = useState("");

  const loadingSlidePreview =
    previewImage === "" ||
    (typeof props.syncSlides === "string"
      ? slideId === props.syncSlides
      : props.syncSlides);

  useEffect(() => {
    async function f() {
      const previews = await getPreviews(props.documentId, 0, 1);
      setPreviewImage(
        previews.length > 0 ? previews[0].s3_preview_url + `&k=${v4()}` : ""
      );
      setSlideId(previews.length > 0 ? previews[0].id : "");
    }

    f();
  }, [props.documentId, props.syncSlides]);

  return (
    <div
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      className="relative max-w-[300px]"
    >
      {hover && (
        <div className="absolute w-full h-full bg-black rounded-md"></div>
      )}
      <div onClick={props.onClick}>
        {hover && (
          <div
            onMouseEnter={() => {
              setHover(true);
            }}
            className="absolute flex items-center gap-3 top-1/2 left-1/2 z-40 px-2 py-1 rounded-md text-white border"
            style={{
              transform: "translate(-50%, -50%)",
            }}
          >
            Open <ArrowUpRight />
          </div>
        )}
        {!loadingSlidePreview ? (
          <img
            role="button"
            className={cn(
              "w-[300px] aspect-video border rounded-md transition-all",
              hover ? "opacity-85" : ""
            )}
            loading="eager"
            src={previewImage}
            alt="slide preview"
          />
        ) : (
          <div className="w-[300px] aspect-video bg-[#0d33f1]/50 animate-pulse rounded-md"></div>
        )}
      </div>
    </div>
  );
}
const documentSections = [{ name: "Slides" }, { name: "Knowledge Base" }];

export function Document() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const documentId = path.split("/")[2];
  const [addingSlide, setAddingSlide] = useState(false);
  let documentResponse = useFullDocument(documentId || "");
  let fullDocumentResponse = useFullDocument(documentId || "", {
    include_questions: true,
    include_knowledge_base: true,
    include_slides: true,
  });

  const [fullDocument, setFullDocument] = useState<any>({});

  useEffect(() => {
    let _fullDocument = fullDocumentResponse.document?.id
      ? fullDocumentResponse
      : documentResponse.document?.id
      ? documentResponse
      : {
          document: null,
          loading: true,
          refetch: () => Promise.resolve(),
        };

    setFullDocument(_fullDocument);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullDocumentResponse.loading, documentResponse.loading]);
  const [knowledge_base_item_ids, setKnowledgeBaseItemIds] = useState<string[]>(
    []
  );

  const [link, setLink] = useState("");

  const [{ previews }, setPreviews] = useState<{
    previews: Array<{
      google_slide_id: string;
      s3_preview_url: string;
      id: string;
    }>;
    loading: boolean;
  }>({ previews: [], loading: false });
  const [nameEdit, setNameEdit] = useState(false);
  const [descriptionEdit, setDescriptionEdit] = useState(false);

  const [selectedImage, setSelectedImage] = useState<number>(-1);

  const [editQuestion, setEditQuestion] = useState<any>(null);
  const [selectedSlide, setSelectedSlide] = useState<string | null>(null);
  const [, setPatch] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<SlideElement>(null);
  const [showSelectType, setShowSelectType] = useState<boolean>(false);

  const [newName, setNewName] = useState(fullDocument.document?.name);
  const [newDescription, setNewDescription] = useState(
    fullDocument.document?.description
  );

  const [, copy] = useCopyToClipboard();

  const [syncingSingleSlide, setSyncingSingleSlide] = useState<string>("");
  const [syncingPresentation, setSyncingPresentation] =
    useState<boolean>(false);

  async function syncSlideWithUI(slideId: string, index: number) {
    setSyncingSingleSlide(slideId);
    const { status } = await syncSingleSlide(slideId);
    if (status === "error") {
      setSyncingSingleSlide("");
      toast({ title: "Slide Sync Error" });
      return;
    }

    if (status === "deleted") {
      const indexOfSlide = fullDocument.document.slides.findIndex(
        (s: any) => s.id === slideId
      );
      const _slides = [...fullDocument.document.slides];
      delete _slides[indexOfSlide];
      setFullDocument((prev: any) => {
        let newSlides = {
          ...prev,
          document: {
            ...prev.document,
            slides: _slides,
          },
        };
        return newSlides;
      });
      setSyncingSingleSlide("");
      return;
    }
    // await getSlides();
    const previews = await getPreviews(
      fullDocument.document.id,
      index,
      index + 1
    );
    const [first] = previews;
    if (!first) {
      setSyncingSingleSlide("");
      toast({ title: "Slide Sync Error", description: "No Preview found" });
      return;
    }

    const urlObj = new URL(first.s3_preview_url);
    const k = urlObj.searchParams.get("k");
    if (!k) {
      urlObj.searchParams.append("k", v4());
    }

    first.s3_preview_url = urlObj.toString();

    await new Promise((r) => setTimeout(r, 3000));
    const updatedSlide = await getSlide(first.id);
    const indexOfSlide = fullDocument.document.slides.findIndex(
      (s: any) => s.id === first.id
    );
    const _slides = [...fullDocument.document.slides];
    _slides[indexOfSlide] = updatedSlide.data;
    setFullDocument((prev: any) => {
      let newSlides = {
        ...prev,
        document: {
          ...prev.document,
          slides: _slides,
        },
      };
      return newSlides;
    });
    await fetch(first.s3_preview_url, { cache: "reload" });

    setPreviews((prev) => {
      const _previews = [...prev.previews];
      _previews[index] = first;
      return {
        ...prev,
        previews: _previews,
      };
    });
    setSyncingSingleSlide("");
    toast({ title: "Slide Synced" });
  }

  async function syncPresentationWithUI() {
    setSyncingPresentation(true);
    const { status } = await syncPresentation(fullDocument.document.id);
    if (status === "error") {
      setSyncingPresentation(false);
      toast({ title: "Presentation Sync Error" });
      return;
    }

    if (status === "deleted") {
      setSyncingPresentation(false);
      toast({ title: "Presentation Deleted" });
      return;
    }

    await new Promise((r) => setTimeout(r, 3000));
    await fullDocumentResponse.refetch();
    setSyncingPresentation(false);
  }

  useEffect(() => {
    if (!documentResponse.loading) {
      setNewName(documentResponse.document?.name);
      setNewDescription(documentResponse.document?.description);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentResponse.loading]);

  const [showLoadingLink, setShowLoadingLink] = useState<boolean>(false);

  useEffect(() => {
    if (link) {
      setShowLoadingLink(false);
    }
  }, [link]);

  const selectedSlideObj = getSelectedSlide();

  const editPortalRouter = {
    showChooseSlideElementType: showSelectType && !selectedType,
    showEditPart: !showSelectType && selectedSlideObj,
  };

  function closeSlidePopup() {
    setSelectedSlide(null);
    setShowSelectType(false);
    setSelectedType(null);
    setNewElement(null);
    setEditDescPortal("");
  }

  const kbsQuery = useKbs(documentId);

  const section = getSection();
  function getSection() {
    const section =
      window.location.href
        .split("#")
        ?.at(1)
        ?.replace("%20", " ")
        ?.replace("%20", " ") || null;

    return section;
  }

  function getPreview(slide_id: string) {
    const p = previews.find(
      (p) => p.google_slide_id === slide_id || p.id === slide_id
    );
    if (!p) return undefined;
    return { ...p, s3_preview_url: p.s3_preview_url };
  }

  function getSelectedSlide() {
    if (!selectedSlide) return null;
    if (!fullDocument.document?.slides) return null;
    return fullDocument.document.slides.find(
      (slide: any) => slide.id === selectedSlide
    );
  }

  const [newElement, setNewElement] = useState<SlideElementData>(null);
  const [addSlidePortalOpen, setAddSlidePortalOpen] = useState<boolean>(false);
  const [slideIndex, setSlideIndex] = useState<string>("1");

  const [editDescPortal, setEditDescPortal] = useState<string>("");

  function getElementType(element: SlideElementData | null): SlideElement {
    if (!element) return null;
    if ("min_chars" in element) {
      return "text";
    } else if ("alt_text" in element && "url" in element) {
      return "image";
    } else if ("table_height" in element) {
      return "table";
    } else if ("spreadsheet_google_document_id" in element) {
      return "chart";
    }
    return null;
  }

  const newElementType = getElementType(newElement);

  const [confirmDeletePortalOpen, setConfirmDeletePortalOpen] =
    useState<string>("");
  const [deletingSlide, setDeletingSlide] = useState<boolean>(false);

  const textPlaceholders = selectedSlideObj
    ? newElementType !== "text"
      ? selectedSlideObj?.text_placeholders
      : [newElement, ...selectedSlideObj.text_placeholders]
    : [];
  const imagePlaceholders = selectedSlideObj
    ? newElementType !== "image"
      ? selectedSlideObj.image_placeholders
      : [newElement, ...selectedSlideObj.image_placeholders]
    : [];
  const tablePlaceholders = selectedSlideObj
    ? newElementType !== "table"
      ? selectedSlideObj.table_placeholders
      : [newElement, ...selectedSlideObj.table_placeholders]
    : [];
  const chartPlaceholders = selectedSlideObj
    ? newElementType !== "chart"
      ? selectedSlideObj.chart_placeholders
      : [newElement, ...selectedSlideObj.chart_placeholders]
    : [];

  const selectedSlidePortal = createPortal(
    <div
      className="absolute bg-zinc-700/80 w-full h-full left-0 right-0 bottom-0"
      style={{ top: window.scrollY }}
      onClick={closeSlidePopup}
    >
      {editPortalRouter.showChooseSlideElementType && (
        <ChooseSlideElementType
          fullDocument={fullDocument}
          setPatch={setPatch}
          setNewElement={setNewElement}
          closeSlidePopup={closeSlidePopup}
          setSelectedType={setSelectedType}
          setShowSelectType={setShowSelectType}
        />
      )}
      {/* {editPortalRouter.showChooseIntegration && (
        <ChooseIntegration
          dashboards={dashboards}
          fullDocument={fullDocument}
          integrations={integrations!}
          selectedDashboard={selectedDashboard}
          chosenIntegration={choosenIntergration}
          loadingDashboards={loadingDashboards}
          closeSlidePopup={closeSlidePopup}
          setSelectedType={setSelectedType}
          setConfigureLooker={setConfigureLooker}
          setSelectedDashboard={setSelectedDashboard}
          setChosenIntegration={setChoosenIntegrations}
        />
      )} */}
      {/* {editPortalRouter.showConfigureIntegration && (
        <ConfigureIntegration
          elements={elements}
          dashboards={dashboards}
          fullDocument={fullDocument}
          integrationId={choosenIntergration}
          selectedChart={selectedChart}
          selectedElement={selectedElement}
          loadingElenents={loadingElements}
          selectedSlideObj={selectedSlideObj}
          selectedDashboard={selectedDashboard}
          setShowSelectType={setShowSelectType}
          setNewElement={setNewElement}
          setPatch={setPatch}
          setSavingElementUpdate={setSavingElementUpdate}
          closeSlidePopup={closeSlidePopup}
          setSelectedType={setSelectedType}
          setSelectedChart={setSelectedChart}
          setConfigureLooker={setConfigureLooker}
          setSelectedElement={setSelectedElement}
          setSelectedDashboard={setSelectedDashboard}
        />
      )} */}
      {editPortalRouter.showEditPart && (
        <EditElement
          newElement={newElement}
          fullDocument={fullDocument}
          selectedSlide={selectedSlide}
          editDescPortal={editDescPortal}
          selectedSlideObj={selectedSlideObj}
          textPlaceholders={textPlaceholders}
          organizationId={fullDocument.document?.organization_id}
          imagePlaceholders={imagePlaceholders}
          chartPlaceholders={chartPlaceholders}
          tablePlaceholders={tablePlaceholders}
          editSlide={editSlide}
          getPreview={getPreview}
          closeSlidePopup={closeSlidePopup}
          setEditDescPortal={setEditDescPortal}
          setShowSelectType={setShowSelectType}
        />
      )}
    </div>,
    document.getElementById("portal")!
  );

  const addSlidePortal = createPortal(
    <div
      className="absolute bg-zinc-700/80 w-full h-full left-0 right-0 bottom-0"
      style={{ top: window.scrollY }}
      onClick={() => {
        setAddSlidePortalOpen(false);
      }}
    >
      {addSlidePortalOpen && (
        <div
          className="bg-white absolute top-1/2 left-1/2 p-5 rounded-md min-w-[500px]"
          onClick={(e) => e.stopPropagation()}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <div className="flex items-start justify-center flex-col">
            <label htmlFor="slideindex" className="">
              Index of slide to add
            </label>
            <p className="mb-2 text-sm text-zinc-500">
              View presentation{" "}
              <span
                className="underline"
                onClick={async () => {
                  if (!documentId) return;
                  if (link) {
                    window.open(link, "_blank");
                    return;
                  }
                  setShowLoadingLink(true);
                  const url = await getDocumentLink(documentId, () => {
                    setShowLoadingLink(false);
                    toast({
                      title: "Error",
                      description: "Failed to generate link",
                      variant: "destructive",
                    });
                  });
                  if (!url) {
                    setShowLoadingLink(false);
                    return;
                  }
                  setLink(url.link);
                  window.open(url.link, "_blank");
                }}
              >
                here
              </span>{" "}
              to find the index
            </p>
            <Input
              type="number"
              value={slideIndex.toString()}
              onChange={(e) => {
                setSlideIndex(e.target.value);
              }}
              onBlur={(e) => {
                if (e.target.value === "" || e.target.value.includes("-")) {
                  setSlideIndex("1");
                }
              }}
            />
          </div>

          <div className="flex justify-between mt-5">
            <div className="flex-1"></div>
            <Button
              className="flex items-center gap-2 disabled:opacity-50"
              disabled={addingSlide}
              onClick={async () => {
                setAddingSlide(true);
                const response = await addSlide(
                  fullDocument.document.id,
                  slideIndex
                );
                setAddingSlide(false);
                if (response.message || response.detail) {
                  toast({
                    title: "Info",
                    description: response.message || response.detail,
                  });
                  return;
                }

                if (response.success) {
                  toast({
                    title: "Slide Added",
                    description: "Slide added successfully",
                  });
                  setAddSlidePortalOpen(false);
                  setSlideIndex("1");
                  await fullDocument.refetch();
                }
              }}
            >
              Done
              {addingSlide && (
                <LoaderCircle size={15} className="animate-spin" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>,
    document.getElementById("portal")!
  );

  const confirmDeletePortal = createPortal(
    <div
      className="absolute bg-zinc-700/80 w-full h-full left-0 right-0 bottom-0"
      style={{ top: window.scrollY }}
      onClick={() => {
        setConfirmDeletePortalOpen("");
      }}
    >
      {confirmDeletePortalOpen !== "" && (
        <div
          className="bg-white absolute top-1/2 left-1/2 p-5 rounded-md min-w-[500px]"
          onClick={(e) => e.stopPropagation()}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <div className="flex flex-col items-start gap-5">
            <div>Are you sure you want to delete this slide?</div>
            <div className="flex justify-end mt-4 w-full">
              <Button
                className="flex items-center gap-2 disabled:opacity-50 bg-white border border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                disabled={deletingSlide}
                onClick={async () => {
                  setDeletingSlide(true);
                  await deleteSlide(confirmDeletePortalOpen);
                  setDeletingSlide(false);

                  toast({
                    title: "Slide Deleted",
                    description: "Slide deleted successfully",
                  });
                  setConfirmDeletePortalOpen("");
                  await fullDocument.refetch();
                }}
              >
                Delete
                {deletingSlide && (
                  <LoaderCircle size={15} className="animate-spin" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.getElementById("portal")!
  );

  async function batchPreviewLoader(slidesLength: number) {
    // set loading to true
    setPreviews((p) => ({ ...p, loading: true }));
    let startIndex = 0;
    let endIndex = 50;

    // batching previews loads
    for (let i = 0; i < slidesLength; i += 50) {
      const previews = await getPreviews(documentId, startIndex, endIndex);

      const newPreviews = previews.map((p) => {
        const urlObj = new URL(p.s3_preview_url);
        const k = urlObj.searchParams.get("k");
        if (!k) {
          urlObj.searchParams.append("k", v4());
        }

        p.s3_preview_url = urlObj.toString();
        return p;
      });

      setPreviews((prev) => ({
        ...prev,
        previews: [...prev.previews, ...newPreviews],
      }));
      startIndex = endIndex;
      endIndex += 50;
    }

    // set loading to false
    setPreviews((p) => ({ ...p, loading: false }));
  }

  useEffect(() => {
    if (fullDocumentResponse.loading) return;
    if (!window.location.href.includes("#")) {
      navigate(`/document/${documentId}#Slides`);
    }
    if (
      fullDocumentResponse.document &&
      fullDocumentResponse.document.knowledge_base_item_ids
    ) {
      setPreviews((p) => ({ ...p, previews: [] }));
      setKnowledgeBaseItemIds(
        fullDocumentResponse.document.knowledge_base_item_ids
      );
      setNewName(fullDocumentResponse.document.name);
      setNewDescription(fullDocumentResponse.document.description);

      const slidesLength = fullDocumentResponse.document.slides
        .length as number;

      batchPreviewLoader(slidesLength);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullDocumentResponse.loading]);

  useEffect(() => {
    if (selectedSlide || selectedImage > -1) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [selectedSlide, selectedImage]);

  const imagePopup = createPortal(
    <div
      className="absolute bg-zinc-700/80 w-full h-full left-0 right-0 bottom-0"
      style={{ top: window.scrollY }}
      onClick={() => {
        setSelectedImage(-1);
      }}
    >
      {selectedImage > -1 && (
        <div
          className="bg-transparent absolute top-1/2 left-1/2 rounded-md flex flex-col items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          style={{ transform: "translate(-50%, -50%)", width: "80%" }}
        >
          <img
            src={
              getPreview(
                fullDocument.document.slides[selectedImage].format_data
                  .google_slide_id ||
                  fullDocument.document.slides[selectedImage].id
              )?.s3_preview_url
            }
            alt="slide preview"
            style={{ width: "100%" }}
            className="rounded-md"
          />
          <div className="text-white flex items-center gap-5 w-full justify-center py-3">
            <button
              onClick={() =>
                setSelectedImage((s) => {
                  if (s - 1 < 0) return fullDocument.document.slides.length - 1;
                  return s - 1;
                })
              }
              className="hover:bg-zinc-100 p-1 rounded-md hover:text-zinc-700 transition-all"
            >
              <ChevronLeft size={20} />
            </button>
            <div>
              <span>
                {selectedImage + 1} / {fullDocument.document.slides.length}
              </span>
            </div>
            <button
              onClick={() =>
                setSelectedImage((s) => {
                  if (s + 1 >= fullDocument.document.slides.length) return 0;
                  return s + 1;
                })
              }
              className="hover:bg-zinc-100 p-1 rounded-md hover:text-zinc-700 transition-all"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.getElementById("portal")!
  );

  return (
    <div className="px-10 py-5 w-full">
      {selectedImage > -1 && imagePopup}
      {confirmDeletePortalOpen && confirmDeletePortal}
      {fullDocument.loading && (
        <div className="flex items-center justify-center py-5">
          <Spinner />
        </div>
      )}
      {selectedSlide && selectedSlidePortal}
      {Object.keys(fullDocument.document || {}).length > 0 && (
        <div className="transition-all">
          <a
            className="inline-flex text-zinc-400 cursor-pointer items-center gap-2 rounded-md hover:text-zinc-700 transition-all"
            href="/"
          >
            <ChevronLeft size={20} />
            <p>All presentations</p>
          </a>
          <div className="flex flex-row-reverse items-center justify-end my-5 max-w-[1500px]">
            <div className="ml-10">
              <div className="flex items-center gap-5 mb-2">
                <div className="flex items-center gap-2 justify-start">
                  {!nameEdit ? (
                    <div className="flex items-center gap-2">
                      <h1 className="font-medium text-xl">
                        {newName || "No name found"}
                      </h1>
                      <div>
                        {fullDocument.document.type === "template" ? (
                          <div className=" bg-[#033df1]/5 text-xs w-fit px-2 text-[#033df1] py-0.5 rounded-full border border-[#033df1]">
                            Template
                          </div>
                        ) : (
                          <div className="bg-green-300/5 text-xs w-fit px-2 text-green-600 py-0.5 rounded-full border border-green-500">
                            Library
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="text-base"
                    />
                  )}
                </div>
                <div>
                  {!nameEdit ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNameEdit((b) => !b);
                      }}
                    >
                      <div className="hidden">Edit</div>
                      <Edit size={15} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-green-500 p-2 rounded-md transition-all bg-green-100 hover:bg-green-200"
                      onClick={async () => {
                        setNameEdit(false);
                        if (newName === fullDocument.document.name) return;
                        await editDocument(documentId, { name: newName });
                        await fullDocument.refetch(false);
                        toast({
                          title: "Document Updated",
                          description: `Document name updated to "${newName}"`,
                        });
                      }}
                    >
                      <div className="hidden">Check</div>
                      <Check size={20} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!descriptionEdit ? (
                  <p className="text-zinc-500 text-sm">
                    <span className="font-bold text-zinc-600">
                      Description:
                    </span>{" "}
                    {newDescription || "No description found"}
                  </p>
                ) : (
                  <Textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    cols={40}
                    onBlur={async () => {
                      setDescriptionEdit(false);
                      if (newDescription === fullDocument.document.description)
                        return;
                      await editDocument(documentId, {
                        description: newDescription,
                      });
                      await fullDocument.refetch(false);
                      toast({
                        title: "Document Updated",
                        description: `Document description updated to "${newDescription}"`,
                      });
                    }}
                  />
                )}
                <div className="flex items-center">
                  {!descriptionEdit ? (
                    <button
                      type="button"
                      onClick={() => {
                        setDescriptionEdit((b) => !b);
                      }}
                    >
                      <div className="hidden">Edit</div>
                      <Edit size={15} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-green-500 p-2 rounded-md transition-all bg-green-100 hover:bg-green-200"
                      onClick={async () => {
                        setDescriptionEdit(false);
                        if (
                          newDescription === fullDocument.document.description
                        )
                          return;
                        await editDocument(documentId, {
                          description: newDescription,
                        });
                        await fullDocument.refetch(false);
                        toast({
                          title: "Document Updated",
                          description: `Document description updated to "${newDescription}"`,
                        });
                      }}
                    >
                      <div className="hidden">Check</div>
                      <Check size={20} />
                    </button>
                  )}
                </div>
              </div>
              <div className="my-2 flex items-center gap-2 text-xs w-fit group">
                <p className="text-zinc-500 text-sm">
                  <span className="font-bold text-zinc-600">Document ID:</span>{" "}
                  <span
                    onClick={() => {
                      if (!fullDocument.document || !fullDocument.document.id) {
                        toast({
                          title: "Document ID not found",
                        });
                        return;
                      }
                      copy(fullDocument.document.id);
                      toast({
                        title: "Document ID copied to clipboard",
                      });
                    }}
                    className=" group-hover:bg-zinc-200 px-1 py-0.5 transition-all cursor-pointer rounded-md"
                  >
                    {fullDocument.document.id?.substring(0, 15) + "..." ||
                      "<No Document ID>"}
                  </span>
                </p>
                <button
                  onClick={() => {
                    if (!fullDocument.document || !fullDocument.document.id) {
                      toast({
                        title: "Document ID not found",
                      });
                      return;
                    }
                    copy(fullDocument.document.id);
                    toast({
                      title: "Document ID copied to clipboard",
                    });
                  }}
                  className="p-1 rounded-md border hover:bg-zinc-200"
                >
                  <ClipboardIcon size={14} />
                </button>
              </div>
            </div>

            <div>
              <PreviewImage
                // link={link}
                // setLink={setLink}
                syncSlides={syncingPresentation || syncingSingleSlide}
                onClick={async () => {
                  if (!documentId) return;
                  if (link) {
                    window.open(link, "_blank");
                    return;
                  }
                  setShowLoadingLink(true);
                  const url = await getDocumentLink(documentId, () => {
                    setShowLoadingLink(false);
                    toast({
                      title: "Error",
                      description: "Failed to generate link",
                      variant: "destructive",
                    });
                  });
                  if (!url) {
                    setShowLoadingLink(false);
                    return;
                  }
                  setLink(url.link);
                  window.open(url.link, "_blank");
                }}
                documentId={documentId}
              />
            </div>
          </div>
          <div className="flex items-center justify-start">
            <div className="border-r border-dashed border-zinc-600 pr-3 max-lg:hidden">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} />
                <p>
                  Status:{" "}
                  <span className="bg-green-100 text-green-500 rounded-md px-3 py-0.5">
                    {fullDocument.document.status === "in_progress"
                      ? "Processing"
                      : "Active"}
                  </span>
                </p>
              </div>
            </div>
            <div className="border-r border-dashed border-zinc-600 pr-3 max-lg:border-none">
              <div
                role="button"
                className="flex items-center gap-2 pl-3 transition-all hover:text-zinc-400"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!documentId) return;
                  if (link) {
                    window.open(link, "_blank");
                    return;
                  }
                  setShowLoadingLink(true);
                  const url = await getDocumentLink(documentId, () => {
                    setShowLoadingLink(false);
                    toast({
                      title: "Error",
                      description: "Failed to generate link",
                      variant: "destructive",
                    });
                  });
                  if (!url) {
                    setShowLoadingLink(false);
                    return;
                  }
                  setLink(url.link);
                  window.open(url.link, "_blank");
                }}
              >
                <ArrowUpRight size={20} />
                <p>View Presentation</p>
              </div>
            </div>
            <div className="border-r border-dashed border-zinc-600 pr-3 max-lg:hidden">
              <div className="flex items-center gap-2 pl-3 transition-all hover:text-zinc-400">
                <CircleUser size={20} />
                <p>
                  Created by: {fullDocument.document.created_by || "Unknown"}
                </p>
              </div>
            </div>
            <div className="border-r border-dashed border-zinc-600 pr-3 max-lg:hidden">
              <div className="flex items-center gap-2 pl-3 transition-all hover:text-zinc-400">
                <Clock size={20} />
                <p>
                  Last updated:{" "}
                  {new Date(fullDocument.document.updated_at).toDateString()}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5 my-10 w-full border-b border-zinc-300 py-4">
            {documentSections.map((section, i) => (
              <div
                key={i}
                className={cn(
                  "cursor-pointer transition-all px-3 py-2 rounded-md",
                  window.location.href.includes(
                    "#" + section.name.replace(" ", "%20")
                  )
                    ? "text-[#0d33f1] font-medium bg-zinc-100"
                    : "text-zinc-500"
                )}
                onClick={() => {
                  navigate(`/document/${documentId}#${section.name}`);
                }}
              >
                <p className="text-lg">{section.name}</p>
              </div>
            ))}
          </div>

          {section === "Slides" && (
            <div className="items-end justify-center flex-col flex w-full">
              {addSlidePortalOpen && addSlidePortal}
              {fullDocument.document &&
                fullDocument.document.format === "google" && (
                  <Button
                    onClick={syncPresentationWithUI}
                    disabled={!!syncingSingleSlide || syncingPresentation}
                    className="mb-5 disabled:opacity-50"
                  >
                    Sync With Google Slides Presentation
                  </Button>
                )}

              <Table>
                <TableHeader className="bg-zinc-100">
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Slide</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Personalizations</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fullDocument.document.slides.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-zinc-400"
                      >
                        {fullDocumentResponse.loading ? (
                          <Loader2
                            className="animate-spin m-auto"
                            size={25}
                            color="#0033df"
                          />
                        ) : (
                          "No slides found"
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {syncingPresentation && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        <div className="py-10 w-7/12 m-auto">
                          <div className="h-[10px] w-full rounded-full bg-[#0d33f1]/50 animate-pulse"></div>
                          <div className="text-center w-full py-2 text-zinc-400">
                            Your document is being synced. This may take a few
                            minutes
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!syncingPresentation &&
                    fullDocument.document.slides.map(
                      (slide: any, i: number) => (
                        <TableRow
                          key={i}
                          className="cursor-pointer"
                          onClick={async () => {
                            // window.scrollTo({ top: 0, behavior: "smooth" });
                            setSelectedSlide(slide.id);
                          }}
                        >
                          <TableCell role="button">{i + 1}</TableCell>
                          <TableCell role="button" className="font-medium">
                            <div className="flex items-center gap-2 flex-shrink-0 flex-grow">
                              {syncingSingleSlide === slide.id ||
                              !getPreview(
                                slide.format_data.google_slide_id || slide.id
                              )?.s3_preview_url ? (
                                <div className="w-[200px] aspect-video rounded-md xl:w-[300px] hover:brightness-75 transition-all bg-[#0d33f1]/40 animate-pulse"></div>
                              ) : (
                                <img
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImage(i);
                                  }}
                                  src={
                                    getPreview(
                                      slide.format_data.google_slide_id ||
                                        slide.id
                                    )?.s3_preview_url || ""
                                  }
                                  className="w-[200px] aspect-video rounded-md xl:w-[300px] hover:brightness-75 transition-all"
                                  alt=""
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {slide.description.substring(0, 100)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {"text_placeholders" in slide &&
                              slide["text_placeholders"].length ? (
                                <div>
                                  <img className="w-8" src="/text.png" alt="" />
                                </div>
                              ) : (
                                ""
                              )}
                              {"image_placeholders" in slide &&
                              slide["image_placeholders"].length ? (
                                <div>
                                  <img
                                    className="w-8"
                                    src="/image.png"
                                    alt=""
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                              {"chart_placeholders" in slide &&
                              slide["chart_placeholders"].length ? (
                                <div>
                                  <img
                                    className="w-8"
                                    src="/chart.png"
                                    alt=""
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                              {"looker_image_placeholders" in slide &&
                              slide["looker_image_placeholders"].length ? (
                                <div>
                                  <img
                                    className="w-7 bg-yellow-100/50 rounded-full p-1.5"
                                    src="/hub.png"
                                    alt=""
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-4 ">
                              {fullDocument.document.format === "google" && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <button
                                      type="button"
                                      className="bg-zinc-100 p-2 rounded-md transition-all hover:text-[#0d33f1] hover:bg-[#0d33f1]/5 disabled:opacity-50"
                                      disabled={!!syncingSingleSlide}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        await syncSlideWithUI(slide.id, i);
                                      }}
                                    >
                                      <div className="hidden">Sync Slide</div>
                                      <RefreshCw
                                        className={
                                          syncingSingleSlide === slide.id
                                            ? "animate-spin"
                                            : ""
                                        }
                                        size={15}
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    data-state="instant-open"
                                    className="w-[350px] bg-white"
                                  >
                                    <p className="text-medium">Info</p>
                                    <p className="text-zinc-500">
                                      Sync this slide with the latest changes
                                      from the Google Slide
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <button
                                type="button"
                                disabled={deletingSlide || !!syncingSingleSlide}
                                className="hover:text-red-400 transition-all bg-zinc-100 p-2 rounded-md disabled:opacity-50"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setConfirmDeletePortalOpen(slide.id);
                                }}
                              >
                                <div className="hidden">Delete</div>
                                <Trash size={15} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                </TableBody>
              </Table>
            </div>
          )}
          {section === "Knowledge Base" && (
            <div className="mt-5">
              {kbsQuery.kbs.length === 0 && (
                <p className="text-zinc-500 text-sm">
                  No knowledge bases found
                </p>
              )}

              <Table>
                <TableHeader className="bg-zinc-100">
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Knowledge Base Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead></TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kbsQuery.kbs.map((kb: any, i: number) => (
                    <TableRow key={i} onClick={async () => {}}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{kb.name}</TableCell>
                      <TableCell>
                        {kb.description.substring(0, 50)}...
                      </TableCell>
                      <TableCell>
                        <div>
                          <Switch
                            checked={knowledge_base_item_ids.includes(kb.id)}
                            onCheckedChange={async (checked) => {
                              setKnowledgeBaseItemIds((prev) =>
                                checked
                                  ? [...prev, kb.id]
                                  : prev.filter((id) => id !== kb.id)
                              );
                              if (checked) {
                                await addKb(documentId, kb.id);
                              } else {
                                // remove it
                                await removeKb(documentId, kb.id);
                              }

                              await fullDocument.refetch(false);

                              toast({
                                title: "Knowledge Base Updated",
                                description: `Knowledge Base "${kb.name}" ${
                                  checked ? "added to" : "removed from"
                                } this document`,
                              });
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={!!editQuestion}
        onOpenChange={(open) => {
          if (!open) {
            setEditQuestion(null);
          }
        }}
      >
        <DialogContent className="text-zinc-700">
          {editQuestion && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const originalQuestion = fullDocument.document.questions.find(
                  (q: any) => q.id === editQuestion.id
                );

                if (!originalQuestion) return;

                if (
                  originalQuestion.header === editQuestion.header &&
                  originalQuestion.description === editQuestion.description &&
                  originalQuestion.answer_type === editQuestion.answer_type
                ) {
                  setEditQuestion(null);
                  return;
                }

                const res = await eQuestion(
                  documentId,
                  editQuestion.id,
                  editQuestion
                );
                if (res.answer_type) {
                  setEditQuestion(null);
                  fullDocument.refetch();
                }
              }}
            >
              <div>
                <label htmlFor="header">Question Header</label>
                <Input
                  type="text"
                  value={editQuestion.header}
                  onChange={(e) => {
                    setEditQuestion((prev: any) => ({
                      ...prev,
                      header: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="mt-3">
                <label htmlFor="description">Question Description</label>
                <Textarea
                  value={editQuestion.description}
                  onChange={(e) => {
                    setEditQuestion((prev: any) => ({
                      ...prev,
                      description: e.target.value,
                    }));
                  }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between w-full my-3">
                  <div className="flex items-center gap-3">
                    <div
                      role="button"
                      onClick={() =>
                        setEditQuestion((prev: any) => ({
                          ...prev,
                          answer_type: "paragraph",
                        }))
                      }
                      className={clsx(
                        "flex items-center gap-3 border rounded-full px-4 py-0.5",
                        editQuestion.answer_type === "paragraph"
                          ? "border-[#0d33f1] text-[#0d33f1] bg-[#0d33f1]/5"
                          : "border-zinc-400 text-zinc-700"
                      )}
                    >
                      <Text size={15} />
                      <p>Text</p>
                    </div>
                    <div
                      role="button"
                      onClick={() =>
                        setEditQuestion((prev: any) => ({
                          ...prev,
                          answer_type: "url",
                        }))
                      }
                      className={clsx(
                        "flex items-center gap-3 border rounded-full px-4 py-0.5",
                        editQuestion.answer_type === "url"
                          ? "border-[#0d33f1] text-[#0d33f1] bg-[#0d33f1]/5"
                          : "border-zinc-400 text-zinc-700"
                      )}
                    >
                      <Link size={15} />
                      <p>URL</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full">
                <Button type="submit" className="w-full">
                  Save
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={showLoadingLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generating File</DialogTitle>
            <DialogDescription>
              Please wait while we generate the file
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center">
            <LoaderCircle className="animate-spin" size={50} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
